import { useState, FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";

const faqs = [
  {
    q: "日韩欧美 Style Hub 是什么？",
    a: "日韩欧美 Style Hub 是一个专注日系、韩系、欧美、美式四大国际风格的一站式时尚内容平台。我们汇聚最新潮流趋势、穿搭指南和单品推荐，帮助你轻松驾驭多元国际风格。",
  },
  {
    q: "如何选择适合自己的风格？",
    a: "建议从自己的身材特点、日常场合和个人偏好出发。日系偏清新自然适合温柔气质，韩系偏时尚精致适合都市女性，欧美偏简约大气适合职场精英，美式偏随性自由适合个性表达。你可以浏览我们的风格分类页面获取更多灵感。",
  },
  {
    q: "网站上的单品在哪里可以购买？",
    a: "我们目前提供的是时尚资讯和穿搭灵感内容。每件推荐单品都会标注参考价格区间，你可以根据品牌和款式信息在各大电商平台搜索购买。我们也在持续优化购物体验，敬请期待。",
  },
  {
    q: "如何订阅最新潮流资讯？",
    a: "你可以在首页底部的Newsletter区域输入邮箱地址订阅我们的潮流速递，每周会收到最新的穿搭灵感、趋势解读和独家内容推送。",
  },
  {
    q: "可以投稿或合作吗？",
    a: "当然可以！我们欢迎时尚博主、品牌和内容创作者与我们合作。请通过联系我们表单发送合作意向，我们的团队会在3个工作日内回复。",
  },
];

const testimonials = [
  {
    name: "小雨",
    role: "时尚博主",
    content: "日韩欧美 Style Hub 是我每天必看的时尚资讯平台。文章质量很高，穿搭建议实用又好上手，已经推荐给了身边所有爱美的小伙伴。",
    rating: 5,
  },
  {
    name: "Emily",
    role: "职场白领",
    content: "作为一个在日企工作的女生，日系穿搭板块对我帮助特别大。从基础款搭配到季节趋势，每一篇文章都让我学到新东西。",
    rating: 5,
  },
  {
    name: "Lucas",
    role: "时尚买手",
    content: "四个风格体系分类清晰，内容专业度很高。美式复古板块尤其出彩，90年代穿搭回归那篇文章写得非常到位。",
    rating: 5,
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const message = formData.get("message") as string;
    if (message && message.length > 500) {
      setFormStatus("error");
      setFormError("留言内容不能超过500字符");
      return;
    }
    try {
      const res = await fetch("https://readdy.ai/api/form/d9qr5kfnahb7382ove0g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ name, email, message }).toString(),
      });
      const responseText = await res.text();
      let serverMsg = "";
      try {
        const parsed = JSON.parse(responseText);
        serverMsg = parsed?.meta?.message || parsed?.message || "";
      } catch {
        serverMsg = responseText;
      }
      if (res.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请稍后再试");
    }
  };

  const canonical = "https://ribenhanou-style.com/about";
  const pageTitle = "关于我们 - 日韩欧美 Style Hub";
  const pageDesc =
    "了解日韩欧美 Style Hub 的品牌故事与使命。汇聚日系、韩系、欧美、美式四大国际风格，为你提供一站式潮流资讯、穿搭指南和风格灵感。联系我们获取更多合作信息。";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        name: pageTitle,
        description: pageDesc,
        url: canonical,
        isPartOf: { "@type": "WebSite", name: "日韩欧美 Style Hub" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ribenhanou-style.com/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: canonical },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords="日韩欧美,关于我们,日韩欧美风格,日韩欧美潮流,联系我们"
        canonicalUrl={canonical}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-20 md:pt-24">
        {/* Header */}
        <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="max-w-7xl mx-auto mt-4 md:mt-6">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              了解日韩欧美 Style Hub 的故事与使命
            </p>
          </div>
        </div>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
                  品牌故事
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  汇聚国际风格，点亮时尚灵感
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  日韩欧美 Style Hub 诞生于2024年，由一群热爱国际时尚的编辑和买手创立。我们发现市场上缺乏一个能同时覆盖日系、韩系、欧美、美式四大风格的综合性潮流平台，于是决定自己做一个。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  我们的使命是打破地域界限，让每一位热爱时尚的人都能在这里找到属于自己的风格灵感。无论你偏爱日系的清新自然、韩系的精致时尚、欧美的大气简约，还是美式的自由随性，这里都有你想要的答案。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  成立至今，我们已经累计发布了超过200篇原创穿搭指南和趋势解读，服务了超过50万追求品质生活的时尚爱好者。未来，我们将继续深耕内容，拓展更多元化的时尚服务。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden aspect-[4/5]">
                <img
                  src="https://readdy.ai/api/search-image?query=Fashion%20editorial%20studio%20with%20minimalist%20aesthetic%2C%20warm%20cream%20and%20beige%20interior%2C%20soft%20natural%20lighting%2C%20fashion%20magazine%20layout%20on%20wooden%20table%2C%20elegant%20atmosphere&width=600&height=750&seq=about-brand-2026&orientation=portrait"
                  alt="日韩欧美 Style Hub 品牌故事"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-accent-100/50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
              {[
                { value: "200+", label: "原创文章" },
                { value: "50万+", label: "活跃读者" },
                { value: "4", label: "风格体系" },
                { value: "98%", label: "读者好评率" },
              ].map((stat) => (
                <div key={stat.label}>
                  <span className="block text-2xl md:text-3xl font-heading font-bold text-accent-700 mb-1">
                    {stat.value}
                  </span>
                  <span className="text-sm text-foreground-600">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4">
                口碑评价
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                读者这样说
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                来自真实用户的评价，是我们前进的最大动力
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-0.5 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <span className="block text-sm font-semibold text-foreground-900">{t.name}</span>
                    <span className="block text-xs text-foreground-500">{t.role}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                期待与你交流
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                有任何问题、建议或合作意向，欢迎给我们留言
              </p>
            </div>

            {formStatus === "success" ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                  发送成功！
                </h3>
                <p className="text-sm text-foreground-600">
                  感谢你的留言，我们会在3个工作日内回复。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form="true"
                className="flex flex-col gap-5"
              >
                <div className="honeypot-wrapper">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入你的留言（最多500字符）"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
                {formStatus === "error" && (
                  <p className="text-sm text-primary-600 text-center">{formError}</p>
                )}
              </form>
            )}

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h4>
                <p className="text-xs text-foreground-600">hello@ribenhanou-style.com</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-wechat-line text-lg text-accent-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                <p className="text-xs text-foreground-600">日韩欧美StyleHub</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-200 flex items-center justify-center">
                  <i className="ri-time-line text-lg text-secondary-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                <p className="text-xs text-foreground-600">周一至周五 9:00-18:00</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-4">
                常见问题
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                你可能想了解
              </h2>
            </div>
            <div className="flex flex-col gap-4">
              {faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between p-5 md:p-6 text-sm md:text-base font-medium text-foreground-900 list-none">
                    {faq.q}
                    <i className="ri-add-line text-foreground-500 group-open:hidden text-sm"></i>
                    <i className="ri-subtract-line text-foreground-500 hidden group-open:block text-sm"></i>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}